import React, { useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import {
  SalesforceIntegrationId,
  SalesforceResourceCreatePayload,
  SalesforceSyncTypes,
} from 'api/integrations';
import { BlockSpace } from 'components/atoms/space';
import { toastService } from 'components/molecules/toast';
import { RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import {
  useSalesforceConfigByIdQuery,
  useSalesForceCreateResourceMutation,
} from 'queries/salesforce-integration';
import { IntegrationStatusTypes } from '../../authentication-types';
import { SalesforceLayout } from '../salesforce-layout';
import { Header } from './header';
import { AddResourceModal } from './modal';
import { ResourceMappingList } from './resource-mapping-list';
import { useFieldMappingActions } from './use-field-mapping-actions';

/**
 * ResourceMappings
 *  |-> Field Object
 *    |-> Field Mapping
 */

export const ResourceMappings = () => {
  const resourceCreationErrorMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.object.create.error.message',
  );
  const errorTitle = useTranslation('failure');

  const { integrationId } = useParams<SalesforceIntegrationId>();

  const {
    mutate: createResourceMutation,
    status: salesForceResourceCreationStatus,
  } = useSalesForceCreateResourceMutation(Number(integrationId));

  const { data } = useSalesforceConfigByIdQuery(Number(integrationId), {
    enabled: integrationId !== 'new',
  });

  const {
    resources,
    resourceMappingsStatus,
    resourceTypeOptions,
    resourceTypeStatus,
    onMappingAdd,
    onMappingRemove,
    onMappingChange,
    onResourceRemove,
    onResourceAdd,
  } = useFieldMappingActions(Number(integrationId));

  const [showAddModal, setShowAddModal] = useState(false);

  const handleSubmit = (resourceType?: string) => {
    setShowAddModal(false);

    if (resourceType) {
      const resourceObject = {
        integrationId: Number(integrationId),
        externalIntegrationType: 'salesforce',
        externalResourceType: resourceType.toString(),
        nativeResourceType: 'project',
        syncType: SalesforceSyncTypes.None,
        enabled: true,
      } as SalesforceResourceCreatePayload;

      createResourceMutation(resourceObject, {
        onSuccess: (resp) => {
          onResourceAdd(resp);
        },
        onError: () => {
          toastService({
            title: errorTitle,
            message: resourceCreationErrorMessage,
          }).error();
        },
      });
    }
  };

  const handleModalCancel = () => {
    setShowAddModal(false);
  };

  if (data?.integrationStatus === IntegrationStatusTypes.Expired) {
    return (
      <Redirect
        push
        to={`${RoutePaths.salesforceIntegrations}/${integrationId}/settings`}
      />
    );
  }

  return (
    <SalesforceLayout>
      <BlockSpace direction="vertical" size={0}>
        <Header showResourceModal={setShowAddModal} />

        <ResourceMappingList
          integrationId={Number(integrationId)}
          resourceMappingsStatus={resourceMappingsStatus}
          resources={resources}
          onMappingAdd={onMappingAdd}
          onMappingRemove={onMappingRemove}
          onMappingChange={onMappingChange}
          onObjectRemove={onResourceRemove}
          showResourceModal={setShowAddModal}
        />
      </BlockSpace>

      {showAddModal ? (
        <AddResourceModal
          visibility={showAddModal}
          resources={resources}
          resourceTypeOptions={resourceTypeOptions}
          resourceTypeStatus={resourceTypeStatus}
          creationStatus={salesForceResourceCreationStatus}
          handleClose={handleModalCancel}
          handleSubmit={handleSubmit}
        />
      ) : null}
    </SalesforceLayout>
  );
};
